/**
 * Tela /templates/drafts — TH-55175 / M-IAtpl.6.
 *
 * Galeria visual dos drafts gerados via IA. O operador acompanha o
 * pipeline (`pending`/`generating` → `draft`/`pending_review`) e
 * cura cada draft: aprova, rejeita (com motivo) ou pede outra
 * tentativa (regerar).
 *
 * Decisões:
 *
 * - Polling automático a cada 5s **enquanto houver draft in-flight**
 *   (`pending` ou `generating`). Implementado no hook
 *   `useTemplateDrafts` via `refetchInterval` calculado a partir do
 *   data atual — para sozinho quando todos chegaram em estado terminal.
 *
 * - Filtros (status, category, style) vivem na query string via
 *   `validateSearch` no router. Persiste a seleção em refresh/share
 *   de URL — pattern já usado em `/custom-arts` e
 *   `/campaigns/templates`.
 *
 * - Ações por card:
 *   • Aprovar — PATCH com status='approved'. Status terminal sem
 *     motivo. Decisão para esta issue: NÃO cria template definitivo
 *     em `templates` (deferred para issue futura — só seta status +
 *     reviewer + reviewed_at).
 *   • Rejeitar — abre modal pedindo motivo (max 500 chars). PATCH
 *     com `rejection_reason` (auditável; ler no backend via
 *     `error_message`).
 *   • Regerar — POST /regenerate cria novo draft com mesmo brief.
 *     Sem confirmação extra; entra na fila e aparece na galeria.
 *
 * - Acessibilidade: cards em grid de botões, `aria-label` em cada
 *   ação, foco visível via Tailwind ring; modal de rejeição usa
 *   Radix Dialog (focus trap + ESC).
 *
 * - Empty state: ponteiro para /templates/generate.
 *
 * - Role gate: super_admin, tenant_admin, operator, catalog_operator
 *   (mesma regra do `_check_can_manage` do backend). Demais roles
 *   veem mensagem de acesso negado.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  CheckCircle2,
  RotateCw,
  Sparkles,
  XCircle,
} from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import { StatusPill, type StatusTone } from "@/components/ui/StatusPill";
import { useAuth } from "@/hooks/useAuth";
import {
  useRegenerateTemplateDraft,
  useReviewTemplateDraft,
  useTemplateDrafts,
  type TemplateDraftCategory,
  type TemplateDraftRead,
  type TemplateDraftStatus,
  type TemplateDraftStyle,
} from "@/hooks/useTemplateDrafts";

export interface TemplateDraftsSearch {
  status?: TemplateDraftStatus;
  category?: TemplateDraftCategory;
  style?: TemplateDraftStyle;
}

const STATUS_OPTIONS: ReadonlyArray<{
  value: "" | TemplateDraftStatus;
  label: string;
}> = [
  { value: "", label: "Todos" },
  { value: "pending", label: "Pendente" },
  { value: "generating", label: "Gerando" },
  { value: "draft", label: "Rascunho" },
  { value: "pending_review", label: "Aguardando revisão" },
  { value: "approved", label: "Aprovado" },
  { value: "rejected", label: "Rejeitado" },
  { value: "failed", label: "Falhou" },
];

const CATEGORY_OPTIONS: ReadonlyArray<{
  value: "" | TemplateDraftCategory;
  label: string;
}> = [
  { value: "", label: "Todas as categorias" },
  { value: "hortifruti", label: "Hortifruti" },
  { value: "acougue", label: "Açougue" },
  { value: "padaria", label: "Padaria" },
  { value: "mercearia", label: "Mercearia" },
  { value: "bebidas", label: "Bebidas" },
  { value: "promo", label: "Promo / Oferta" },
  { value: "limpeza", label: "Limpeza" },
  { value: "sazonal", label: "Sazonal" },
];

const STYLE_OPTIONS: ReadonlyArray<{
  value: "" | TemplateDraftStyle;
  label: string;
}> = [
  { value: "", label: "Todos os estilos" },
  { value: "minimalista", label: "Minimalista" },
  { value: "complexo", label: "Complexo" },
  { value: "combo_a_b", label: "Combo A+B" },
  { value: "sazonal", label: "Sazonal" },
];

// Mapeamento status → tone visual + label final exibido na pill.
const STATUS_VIEW: Record<
  TemplateDraftStatus,
  { tone: StatusTone; label: string }
> = {
  pending: { tone: "muted", label: "Pendente" },
  generating: { tone: "warn", label: "Gerando…" },
  draft: { tone: "warn", label: "Rascunho" },
  pending_review: { tone: "warn", label: "Aguardando" },
  approved: { tone: "success", label: "Aprovado" },
  rejected: { tone: "destructive", label: "Rejeitado" },
  failed: { tone: "destructive", label: "Falhou" },
};

const CATEGORY_LABEL: Record<TemplateDraftCategory, string> = {
  hortifruti: "Hortifruti",
  acougue: "Açougue",
  padaria: "Padaria",
  mercearia: "Mercearia",
  bebidas: "Bebidas",
  promo: "Promo",
  limpeza: "Limpeza",
  sazonal: "Sazonal",
};

const STYLE_LABEL: Record<TemplateDraftStyle, string> = {
  minimalista: "Minimalista",
  complexo: "Complexo",
  combo_a_b: "Combo A+B",
  sazonal: "Sazonal",
};

function brlFmt(value: number | string): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function trimPrompt(text: string, max = 80): string {
  if (!text) return "—";
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + "…";
}

// Status considerados terminais e elegíveis para curadoria.
const REVIEWABLE_STATUSES: ReadonlySet<TemplateDraftStatus> = new Set([
  "draft",
  "pending_review",
]);

const IN_FLIGHT_STATUSES: ReadonlySet<TemplateDraftStatus> = new Set([
  "pending",
  "generating",
]);

/** Card de 1 draft com ações inline. */
function DraftCard({
  draft,
  onReject,
  onApprove,
  onRegenerate,
  isMutating,
}: {
  draft: TemplateDraftRead;
  onReject: (draft: TemplateDraftRead) => void;
  onApprove: (draft: TemplateDraftRead) => void;
  onRegenerate: (draft: TemplateDraftRead) => void;
  isMutating: boolean;
}) {
  const view = STATUS_VIEW[draft.status];
  const canReview = REVIEWABLE_STATUSES.has(draft.status);
  const canRegen =
    draft.status !== "pending" &&
    draft.status !== "generating" &&
    draft.status !== "approved";
  const inFlight = IN_FLIGHT_STATUSES.has(draft.status);

  return (
    <article
      className="flex flex-col overflow-hidden rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-card)]"
      data-testid={`draft-card-${draft.id}`}
      aria-labelledby={`draft-title-${draft.id}`}
    >
      <div className="relative aspect-[4/3] bg-[var(--color-muted-tint)]">
        {draft.preview_url ? (
          <img
            src={draft.preview_url}
            alt={`Preview do draft ${draft.id}`}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : inFlight ? (
          <div
            data-testid={`draft-skeleton-${draft.id}`}
            className="flex h-full w-full items-center justify-center text-xs text-[var(--color-muted-foreground)]"
          >
            <span className="inline-flex items-center gap-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--color-warning)]" />
              Gerando…
            </span>
          </div>
        ) : draft.status === "failed" ? (
          <div className="flex h-full w-full items-center justify-center text-xs text-[var(--color-destructive)]">
            Falhou
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-[var(--color-muted-foreground)]">
            Sem preview
          </div>
        )}
        <div className="absolute right-2 top-2">
          <StatusPill tone={view.tone}>{view.label}</StatusPill>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <header className="flex items-start justify-between gap-2">
          <h3
            id={`draft-title-${draft.id}`}
            className="text-sm font-medium text-[var(--color-foreground)]"
          >
            {draft.category ? CATEGORY_LABEL[draft.category] : "—"}
            {draft.style ? (
              <span className="ml-1 text-[var(--color-muted-foreground)]">
                · {STYLE_LABEL[draft.style]}
              </span>
            ) : null}
          </h3>
        </header>

        <p
          className="text-xs text-[var(--color-muted-foreground)]"
          data-testid={`draft-prompt-${draft.id}`}
        >
          {trimPrompt(draft.prompt_text_preview || draft.ai_prompt)}
        </p>

        {draft.status === "rejected" && draft.error_message ? (
          <p
            role="note"
            className="rounded-md border border-[var(--color-destructive)]/50 bg-[var(--color-destructive)]/10 px-2 py-1 text-xs text-[var(--color-destructive)]"
            data-testid={`draft-reject-reason-${draft.id}`}
          >
            Motivo: {draft.error_message}
          </p>
        ) : null}

        <dl className="grid grid-cols-2 gap-1 text-xs text-[var(--color-muted-foreground)]">
          <div>
            <dt className="sr-only">Custo</dt>
            <dd className="font-mono tabular-nums">
              {brlFmt(draft.ai_cost_brl)}
            </dd>
          </div>
          <div className="text-right">
            <dt className="sr-only">Criado em</dt>
            <dd>{formatDate(draft.created_at)}</dd>
          </div>
        </dl>

        <div className="mt-auto flex flex-wrap gap-2 pt-2">
          <Button
            type="button"
            variant="primary"
            disabled={!canReview || isMutating}
            onClick={() => onApprove(draft)}
            aria-label={`Aprovar draft ${draft.id}`}
            data-testid={`btn-approve-${draft.id}`}
          >
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" aria-hidden />
            Aprovar
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={!canReview || isMutating}
            onClick={() => onReject(draft)}
            aria-label={`Rejeitar draft ${draft.id}`}
            data-testid={`btn-reject-${draft.id}`}
          >
            <XCircle className="mr-1 h-3.5 w-3.5" aria-hidden />
            Rejeitar
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={!canRegen || isMutating}
            onClick={() => onRegenerate(draft)}
            aria-label={`Regerar draft ${draft.id}`}
            data-testid={`btn-regenerate-${draft.id}`}
          >
            <RotateCw className="mr-1 h-3.5 w-3.5" aria-hidden />
            Regerar
          </Button>
        </div>
      </div>
    </article>
  );
}

export function TemplateDraftsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as TemplateDraftsSearch;

  // Role gate — mesma regra de _check_can_manage no backend.
  const canManage =
    user?.role === "super_admin" ||
    user?.role === "tenant_admin" ||
    user?.role === "operator" ||
    user?.role === "catalog_operator";

  const filters = useMemo(
    () => ({
      status: search.status,
      category: search.category,
      style: search.style,
    }),
    [search.status, search.category, search.style],
  );

  const draftsQuery = useTemplateDrafts(filters);
  const reviewMutation = useReviewTemplateDraft();
  const regenMutation = useRegenerateTemplateDraft();

  // Estado do modal de rejeição.
  const [rejectingDraft, setRejectingDraft] = useState<TemplateDraftRead | null>(
    null,
  );
  const [rejectReason, setRejectReason] = useState("");
  const [flash, setFlash] = useState<{
    kind: "ok" | "err";
    text: string;
  } | null>(null);

  function updateFilter<K extends keyof TemplateDraftsSearch>(
    key: K,
    value: TemplateDraftsSearch[K] | undefined,
  ): void {
    const next: TemplateDraftsSearch = { ...search };
    // Callers convertem "" → undefined via (e.target.value || undefined),
    // então value nunca chega aqui como string vazia.
    if (value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    void navigate({ to: "/templates/drafts", search: next });
  }

  async function handleApprove(draft: TemplateDraftRead) {
    try {
      await reviewMutation.mutateAsync({ id: draft.id, status: "approved" });
      setFlash({ kind: "ok", text: "Draft aprovado." });
    } catch (err) {
      setFlash({
        kind: "err",
        text:
          err instanceof Error
            ? err.message
            : "Não foi possível aprovar o draft.",
      });
    }
  }

  async function handleRejectSubmit() {
    if (!rejectingDraft) return;
    if (!rejectReason.trim()) return;
    try {
      await reviewMutation.mutateAsync({
        id: rejectingDraft.id,
        status: "rejected",
        rejection_reason: rejectReason.trim(),
      });
      setFlash({ kind: "ok", text: "Draft rejeitado." });
      setRejectingDraft(null);
      setRejectReason("");
    } catch (err) {
      setFlash({
        kind: "err",
        text:
          err instanceof Error
            ? err.message
            : "Não foi possível rejeitar o draft.",
      });
    }
  }

  async function handleRegenerate(draft: TemplateDraftRead) {
    try {
      const resp = await regenMutation.mutateAsync(draft.id);
      setFlash({
        kind: "ok",
        text: `Novo draft enfileirado (custo estimado ${brlFmt(
          resp.estimated_cost_brl,
        )}).`,
      });
    } catch (err) {
      setFlash({
        kind: "err",
        text:
          err instanceof Error
            ? err.message
            : "Não foi possível regerar o draft.",
      });
    }
  }

  if (user && !canManage) {
    return (
      <RequireAuth>
        <AppLayout
          breadcrumb="OPERAÇÃO / TEMPLATES / DRAFTS"
          title="Acesso negado"
        >
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Apenas papéis administrativos podem revisar drafts de templates.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  const drafts = draftsQuery.data ?? [];
  const isMutating = reviewMutation.isPending || regenMutation.isPending;

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / TEMPLATES / DRAFTS"
        title="Galeria de drafts"
      >
        <div className="mx-auto max-w-7xl space-y-5">
          <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Drafts gerados via IA. Aprovados viram templates oficiais;
              rejeitados ficam auditáveis com motivo.
            </p>
            <Button
              type="button"
              variant="primary"
              onClick={() => navigate({ to: "/templates/generate" })}
              data-testid="btn-go-generate"
            >
              <Sparkles className="mr-1 h-4 w-4" aria-hidden />
              Gerar novos drafts
            </Button>
          </header>

          {/* Filtros — query string + select controlado. */}
          <section
            className="grid grid-cols-1 gap-3 rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-card)] p-3 sm:grid-cols-3"
            aria-labelledby="drafts-filters"
            data-testid="drafts-filters"
          >
            <h2 id="drafts-filters" className="sr-only">
              Filtros
            </h2>
            <div className="space-y-1">
              <Label htmlFor="filter-status">Status</Label>
              <Select
                id="filter-status"
                value={search.status ?? ""}
                onChange={(e) =>
                  updateFilter(
                    "status",
                    (e.target.value || undefined) as
                      | TemplateDraftStatus
                      | undefined,
                  )
                }
                data-testid="filter-status"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="filter-category">Categoria</Label>
              <Select
                id="filter-category"
                value={search.category ?? ""}
                onChange={(e) =>
                  updateFilter(
                    "category",
                    (e.target.value || undefined) as
                      | TemplateDraftCategory
                      | undefined,
                  )
                }
                data-testid="filter-category"
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="filter-style">Estilo</Label>
              <Select
                id="filter-style"
                value={search.style ?? ""}
                onChange={(e) =>
                  updateFilter(
                    "style",
                    (e.target.value || undefined) as
                      | TemplateDraftStyle
                      | undefined,
                  )
                }
                data-testid="filter-style"
              >
                {STYLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
          </section>

          {flash ? (
            <p
              role="status"
              data-testid="drafts-flash"
              className={
                flash.kind === "ok"
                  ? "rounded-md border border-[var(--color-success)] bg-[var(--color-success)]/10 px-3 py-2 text-sm text-[var(--color-success)]"
                  : "rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              }
            >
              {flash.text}
            </p>
          ) : null}

          {draftsQuery.isLoading ? (
            <p
              role="status"
              className="text-sm text-[var(--color-muted-foreground)]"
            >
              Carregando drafts…
            </p>
          ) : draftsQuery.error ? (
            <p
              role="alert"
              data-testid="drafts-error"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
            >
              {draftsQuery.error instanceof Error
                ? draftsQuery.error.message
                : "Falha ao carregar drafts."}
            </p>
          ) : drafts.length === 0 ? (
            <div
              data-testid="drafts-empty"
              className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-card)] p-8 text-center"
            >
              <p className="text-sm text-[var(--color-foreground)]">
                Nenhum draft ainda.
              </p>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Use{" "}
                <Link
                  to="/templates/generate"
                  className="text-[var(--color-primary)] hover:underline"
                >
                  /templates/generate
                </Link>{" "}
                pra criar.
              </p>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              data-testid="drafts-grid"
            >
              {drafts.map((d) => (
                <DraftCard
                  key={d.id}
                  draft={d}
                  isMutating={isMutating}
                  onApprove={(draft) => void handleApprove(draft)}
                  onReject={(draft) => {
                    setRejectingDraft(draft);
                    setRejectReason("");
                  }}
                  onRegenerate={(draft) => void handleRegenerate(draft)}
                />
              ))}
            </div>
          )}

          <p className="text-xs text-[var(--color-muted-foreground)]">
            Status em pendente/gerando atualizam automaticamente a cada 5s.
            <Badge variant="muted" className="ml-2">
              {drafts.length} draft(s)
            </Badge>
          </p>
        </div>

        {/* Modal de rejeição. */}
        <Dialog
          open={!!rejectingDraft}
          onOpenChange={(open) => {
            if (!open) {
              setRejectingDraft(null);
              setRejectReason("");
            }
          }}
        >
          <DialogContent data-testid="reject-dialog">
            <DialogTitle>Rejeitar draft</DialogTitle>
            <DialogDescription>
              Descreva o motivo da rejeição. O autor do draft verá esta
              mensagem.
            </DialogDescription>
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                void handleRejectSubmit();
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="reject-reason">Motivo</Label>
                <textarea
                  id="reject-reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  maxLength={500}
                  required
                  rows={4}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-input)] px-3 py-2 text-sm shadow-sm focus:border-[var(--color-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/50"
                  placeholder="Ex.: paleta fora da identidade visual."
                  data-testid="reject-reason-input"
                />
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {rejectReason.length}/500
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setRejectingDraft(null)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!rejectReason.trim() || reviewMutation.isPending}
                  data-testid="reject-confirm"
                >
                  Confirmar rejeição
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </AppLayout>
    </RequireAuth>
  );
}
